import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Mail, MailOpen, Trash2, Loader2 } from "lucide-react";

const inquiryLabels = {
  issuers: "Issuers & Transfer Agents",
  institutions: "Financial Institutions",
  infrastructure: "Infrastructure & Chain Partners",
  builders: "Builders & Community",
};

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const r = await api.get("/admin/contacts");
    setItems(r.data.messages);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const onSelect = async (m) => {
    setActive(m);
    if (!m.is_read) {
      await api.patch(`/admin/contacts/${m.message_id}`, { is_read: true });
      setItems((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, is_read: true } : x));
    }
  };

  const onToggleRead = async (m) => {
    await api.patch(`/admin/contacts/${m.message_id}`, { is_read: !m.is_read });
    setItems((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, is_read: !x.is_read } : x));
    if (active?.message_id === m.message_id) setActive({ ...m, is_read: !m.is_read });
  };

  const onDelete = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/contacts/${m.message_id}`);
    if (active?.message_id === m.message_id) setActive(null);
    reload();
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleString(); } catch { return ""; } };

  return (
    <div data-testid="admin-contacts-page">
      <header className="mb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Inbox</p>
        <h1 className="font-serif text-3xl text-aubergine">Contact submissions</h1>
        <p className="mt-2 text-sm text-aubergine/65">Inquiries from the public contact form.</p>
      </header>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2 rounded-lg border border-aubergine/10 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-aubergine/10 bg-bone/40 flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55">{items.length} message{items.length === 1 ? "" : "s"}</p>
            <p className="font-mono text-[10px] text-aubergine/55">{items.filter((m) => !m.is_read).length} unread</p>
          </div>
          {loading ? (
            <div className="py-16 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-aubergine/55" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-aubergine/55 text-sm" data-testid="contacts-empty">No messages yet.</div>
          ) : (
            <ul className="divide-y divide-aubergine/10 max-h-[70vh] overflow-y-auto">
              {items.map((m) => (
                <li key={m.message_id}>
                  <button
                    onClick={() => onSelect(m)}
                    data-testid={`contact-item-${m.message_id}`}
                    className={`w-full text-left px-4 py-3 transition-colors ${active?.message_id === m.message_id ? "bg-champagne/10" : "hover:bg-bone/40"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${m.is_read ? "text-aubergine/65" : "font-semibold text-aubergine"}`}>{m.name}</span>
                      {!m.is_read && <span className="h-2 w-2 rounded-full bg-champagne" />}
                    </div>
                    <p className="text-xs text-aubergine/55 truncate">{m.company} · {inquiryLabels[m.inquiry_type] || m.inquiry_type}</p>
                    <p className="text-[11px] text-aubergine/45 mt-1">{formatDate(m.created_at)}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-3">
          {active ? (
            <div className="rounded-lg border border-aubergine/10 bg-white p-6" data-testid="contact-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-champagne mb-1">{inquiryLabels[active.inquiry_type] || active.inquiry_type}</p>
                  <h2 className="font-serif text-2xl text-aubergine">{active.name}</h2>
                  <p className="text-sm text-aubergine/65 mt-1">{active.email} · {active.company}{active.role ? ` · ${active.role}` : ""}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => onToggleRead(active)} data-testid="contact-toggle-read" className="rounded p-2 text-aubergine/55 hover:text-aubergine hover:bg-aubergine/5" title={active.is_read ? "Mark unread" : "Mark read"}>
                    {active.is_read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                  </button>
                  <button onClick={() => onDelete(active)} data-testid="contact-delete" className="rounded p-2 text-aubergine/55 hover:text-signal-red hover:bg-signal-red/5" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mt-4 text-[11px] font-mono uppercase tracking-wider text-aubergine/45">{formatDate(active.created_at)}</p>
              <hr className="my-5 border-aubergine/10" />
              <p className="text-sm text-aubergine whitespace-pre-line leading-relaxed">{active.message}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                <a href={`mailto:${active.email}?subject=Re: T-REX Network inquiry`} data-testid="contact-reply" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs">Reply by email</a>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-aubergine/15 p-16 text-center text-aubergine/55 text-sm">
              Select a message to view details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
